import { useState } from 'react';
import type { TrackedResident, ResidentStatus, LatLng } from '@/shared/firebase/types';
import { StatusBadge } from '@/shared/components/StatusBadge';
import { Avatar } from '@/shared/components/Avatar';
import { timeAgo } from '@/shared/utils/time';
import { saveGeofence, clearGeofence } from '@/shared/firebase/writes';

export type PanelMode = 'info' | 'geofence' | 'history';

export type EditingGeofence = { center: LatLng; radius: number };

const HISTORY_RANGES = [
  { key: '1h',  label: '1 h',  ms: 1 * 60 * 60 * 1000 },
  { key: '24h', label: '24 h', ms: 24 * 60 * 60 * 1000 },
  { key: '7d',  label: '7 d',  ms: 7 * 24 * 60 * 60 * 1000 },
] as const;

export type HistoryRangeKey = typeof HISTORY_RANGES[number]['key'];

type Props = {
  centerId: string;
  resident: TrackedResident;
  status: ResidentStatus;
  mode: PanelMode;
  setMode: (m: PanelMode) => void;
  editingGeofence: EditingGeofence | null;
  setEditingGeofence: (g: EditingGeofence | null) => void;
  historyRange: HistoryRangeKey;
  setHistoryRange: (k: HistoryRangeKey) => void;
  historyCount: number;
  onClose: () => void;
};

export function ResidentDetailPanel({
  centerId, resident, status, mode, setMode,
  editingGeofence, setEditingGeofence,
  historyRange, setHistoryRange, historyCount, onClose,
}: Props) {
  const name = resident.config?.name || resident.uid;
  const latest = resident.latest;
  const gf = resident.config?.geofence;

  return (
    <aside className="absolute md:relative inset-0 md:inset-auto md:w-[380px] bg-white border-l border-slate-100 shadow-lift z-20 flex flex-col">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <Avatar name={name} photo={resident.config?.photo} size={36} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900 truncate">{name}</p>
          <StatusBadge status={status} />
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
      </header>

      <nav className="flex border-b border-slate-100">
        {(['info','geofence','history'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            className={`flex-1 py-2.5 text-sm font-semibold transition
              ${mode === k ? 'text-brand border-b-2 border-brand' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {k === 'info' ? 'Info' : k === 'geofence' ? 'Zona' : 'Historial'}
          </button>
        ))}
      </nav>

      <div className="flex-1 overflow-y-auto p-4">
        {mode === 'info' && (
          <InfoTab resident={resident} />
        )}
        {mode === 'geofence' && (
          <GeofenceTab
            centerId={centerId}
            uid={resident.uid}
            currentGeofence={gf}
            editing={editingGeofence}
            setEditing={setEditingGeofence}
            latestPos={latest ? { lat: latest.lat, lng: latest.lng } : null}
          />
        )}
        {mode === 'history' && (
          <HistoryTab
            range={historyRange}
            setRange={setHistoryRange}
            count={historyCount}
          />
        )}
      </div>
    </aside>
  );
}

function InfoTab({ resident }: { resident: TrackedResident }) {
  const latest = resident.latest;
  const alerts = resident.alerts ? Object.entries(resident.alerts).sort(([,a],[,b]) => b.ts - a.ts) : [];
  return (
    <div className="space-y-4">
      <Section title="Última señal">
        {latest ? (
          <ul className="text-sm space-y-1 text-slate-700">
            <li><span className="text-slate-500">Coordenadas:</span> {latest.lat.toFixed(5)}, {latest.lng.toFixed(5)}</li>
            <li><span className="text-slate-500">Hace:</span> {timeAgo(latest.ts)}</li>
            {latest.battery != null && <li><span className="text-slate-500">Batería:</span> {Math.round(latest.battery)}%</li>}
            {latest.speed != null && <li><span className="text-slate-500">Velocidad:</span> {latest.speed.toFixed(1)} km/h</li>}
          </ul>
        ) : <p className="text-sm text-slate-500">Sin datos.</p>}
      </Section>

      <Section title={`Alertas recientes (${alerts.length})`}>
        {alerts.length === 0 ? (
          <p className="text-sm text-slate-500">Sin alertas.</p>
        ) : (
          <ul className="space-y-1.5">
            {alerts.slice(0, 8).map(([id, a]) => (
              <li key={id} className="text-sm flex items-center gap-2">
                <span className="text-base" aria-hidden>{alertIcon(a.type)}</span>
                <span className="flex-1">{a.message || a.type}</span>
                <span className="text-xs text-slate-400">{timeAgo(a.ts)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function GeofenceTab({
  centerId, uid, currentGeofence, editing, setEditing, latestPos,
}: {
  centerId: string; uid: string;
  currentGeofence?: { center: LatLng; radius: number };
  editing: EditingGeofence | null;
  setEditing: (g: EditingGeofence | null) => void;
  latestPos: LatLng | null;
}) {
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setEditing(
      currentGeofence
        ? { center: currentGeofence.center, radius: currentGeofence.radius }
        : { center: latestPos || { lat: 40.4168, lng: -3.7038 }, radius: 100 },
    );
  }

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      await saveGeofence(centerId, uid, editing);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm('¿Eliminar la zona segura de este residente?')) return;
    setSaving(true);
    try {
      await clearGeofence(centerId, uid);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="space-y-4">
        <Section title="Zona segura">
          {currentGeofence ? (
            <ul className="text-sm space-y-1 text-slate-700">
              <li><span className="text-slate-500">Centro:</span> {currentGeofence.center.lat.toFixed(5)}, {currentGeofence.center.lng.toFixed(5)}</li>
              <li><span className="text-slate-500">Radio:</span> {currentGeofence.radius} m</li>
            </ul>
          ) : <p className="text-sm text-slate-500">Sin zona definida.</p>}
        </Section>

        <div className="flex gap-2">
          <button onClick={startEdit} className="btn-primary flex-1">
            {currentGeofence ? 'Editar zona' : 'Crear zona'}
          </button>
          {currentGeofence && (
            <button onClick={remove} disabled={saving} className="btn-danger">Borrar</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Arrastra el círculo en el mapa para mover el centro. Ajusta el radio con el deslizador.
      </p>

      <div>
        <label className="label flex items-center justify-between">
          <span>Radio</span>
          <span className="text-brand font-bold">{editing.radius} m</span>
        </label>
        <input
          type="range" min={20} max={1000} step={10}
          value={editing.radius}
          onChange={(e) => setEditing({ ...editing, radius: Number(e.target.value) })}
          className="w-full accent-brand"
        />
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="btn-primary flex-1">
          {saving ? 'Guardando…' : 'Guardar zona'}
        </button>
        <button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
      </div>
    </div>
  );
}

function HistoryTab({ range, setRange, count }: { range: HistoryRangeKey; setRange: (k: HistoryRangeKey) => void; count: number }) {
  return (
    <div className="space-y-4">
      <Section title="Periodo">
        <div className="grid grid-cols-3 gap-2">
          {HISTORY_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`rounded-xl border py-2 text-sm font-semibold transition
                ${range === r.key ? 'bg-brand text-white border-brand' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
            >{r.label}</button>
          ))}
        </div>
      </Section>

      <Section title="Resultado">
        <p className="text-sm text-slate-700">
          {count === 0
            ? 'Sin posiciones en este periodo.'
            : <>Se han cargado <b className="text-brand">{count}</b> posiciones. La ruta se dibuja en el mapa.</>}
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{title}</h3>
      {children}
    </section>
  );
}

function alertIcon(t: string) {
  if (t === 'sos') return '🆘';
  if (t === 'geofence_out') return '⚠️';
  if (t === 'geofence_in') return '✅';
  if (t === 'low_battery') return '🔋';
  return '📍';
}
